import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHead, Card } from "@/admin/ui";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const ListsAdmin = () => {
  const [news, setNews] = useState([]);
  const [wait, setWait] = useState([]);
  const [launch, setLaunch] = useState([]);
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    api.get("/admin/newsletter").then((r) => setNews(r.data)).catch(() => {});
    api.get("/admin/waitlist").then((r) => setWait(r.data)).catch(() => {});
    api.get("/admin/launch").then((r) => setLaunch(r.data.subscribers || [])).catch(() => {});
    api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, []);

  const removeNews = async (email) => {
    if (!window.confirm(`Remove ${email} from the newsletter list?`)) return;
    const { data } = await api.post("/admin/newsletter/remove", { email });
    setNews(data); toast.success("Removed");
  };
  const removeWait = async (email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    const { data } = await api.post("/admin/waitlist/remove", { email });
    setWait(data); toast.success("Removed");
  };
  const removeLaunch = async (email) => {
    if (!window.confirm(`Remove ${email} from the launch signups?`)) return;
    const { data } = await api.post("/admin/launch/remove", { email });
    setLaunch(data.subscribers || []); toast.success("Removed");
  };

  const csv = (rows, name) => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]);
    const body = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${name}.csv`; a.click();
  };

  const exportAll = () => {
    const rows = [
      ...launch.map((l) => ({ list: "launch", email: l.email, detail: "", status: l.notified ? "notified" : "waiting", at: l.at || "" })),
      ...news.map((n) => ({ list: "newsletter", email: n.email, detail: n.source || "", status: "", at: n.at || "" })),
      ...wait.map((w) => ({ list: "waitlist", email: w.email, detail: [w.product_slug, w.colour, w.size].filter(Boolean).join(" / "), status: "", at: w.at || "" })),
    ];
    if (!rows.length) { toast("No contacts to export yet."); return; }
    const keys = ["list", "email", "detail", "status", "at"];
    const body = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "all-contacts.csv"; a.click();
    toast.success(`Exported ${rows.length} contacts`);
  };

  return (
    <div>
      <div className="flex justify-between items-start flex-wrap gap-3">
        <PageHead title="Customers & Lists" sub="Launch signups, newsletter, waitlist and audit log." />
        <button onClick={exportAll} data-testid="export-all-contacts"
          className="text-[0.66rem] tracking-widest uppercase bg-espresso text-cream px-4 py-2.5 hover:bg-cocoa transition-colors shrink-0">Export all contacts</button>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Card>
          <div className="flex justify-between items-center mb-3">
            <p className="overline text-espresso/50">Launch signups ({launch.length})</p>
            <button onClick={() => csv(launch, "launch-signups")} className="text-[0.62rem] tracking-widest uppercase border border-espresso px-2 py-1">Export CSV</button>
          </div>
          <ul className="text-sm space-y-1 text-espresso/70 max-h-72 overflow-y-auto">
            {launch.map((l, i) => (
              <li key={i} className="flex items-center justify-between gap-2 group" data-testid={`launch-row-${i}`}>
                <span>{l.email} <span className="text-espresso/40 text-xs">· {l.notified ? "notified" : "waiting"}</span></span>
                <button onClick={() => removeLaunch(l.email)} data-testid={`launch-remove-${i}`}
                  className="text-wine opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              </li>
            ))}
            {!launch.length && <li className="text-espresso/40">No launch signups yet.</li>}
          </ul>
        </Card>
        <Card>
          <div className="flex justify-between items-center mb-3">
            <p className="overline text-espresso/50">Newsletter ({news.length})</p>
            <button onClick={() => csv(news, "newsletter")} className="text-[0.62rem] tracking-widest uppercase border border-espresso px-2 py-1">Export CSV</button>
          </div>
          <ul className="text-sm space-y-1 text-espresso/70 max-h-72 overflow-y-auto">
            {news.map((n, i) => (
              <li key={i} className="flex items-center justify-between gap-2 group" data-testid={`newsletter-row-${i}`}>
                <span>{n.email} <span className="text-espresso/40 text-xs">· {n.source}</span></span>
                <button onClick={() => removeNews(n.email)} data-testid={`newsletter-remove-${i}`}
                  className="text-wine opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              </li>
            ))}
            {!news.length && <li className="text-espresso/40">No signups yet.</li>}
          </ul>
        </Card>
        <Card>
          <div className="flex justify-between items-center mb-3">
            <p className="overline text-espresso/50">Waitlist ({wait.length})</p>
            <button onClick={() => csv(wait, "waitlist")} className="text-[0.62rem] tracking-widest uppercase border border-espresso px-2 py-1">Export CSV</button>
          </div>
          <ul className="text-sm space-y-1 text-espresso/70 max-h-72 overflow-y-auto">
            {wait.map((w, i) => (
              <li key={i} className="flex items-center justify-between gap-2 group" data-testid={`waitlist-row-${i}`}>
                <span>{w.email} <span className="text-espresso/40 text-xs">· {w.product_slug} {w.colour} {w.size}</span></span>
                <button onClick={() => removeWait(w.email)} data-testid={`waitlist-remove-${i}`}
                  className="text-wine opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              </li>
            ))}
            {!wait.length && <li className="text-espresso/40">No waitlist entries.</li>}
          </ul>
        </Card>
      </div>
      <Card>
        <p className="overline text-espresso/50 mb-3">Audit Log</p>
        <ul className="text-sm space-y-1 text-espresso/70 max-h-96 overflow-y-auto">
          {audit.map((a, i) => <li key={i}><span className="text-espresso/40 text-xs">{a.at?.slice(0, 16).replace("T", " ")}</span> · {a.user} · <span className="font-medium">{a.action}</span> {a.detail}</li>)}
          {!audit.length && <li className="text-espresso/40">No activity yet.</li>}
        </ul>
      </Card>
    </div>
  );
};

export default ListsAdmin;
